/**
 * forge-skin-runtime — load/cache/apply SkinPacks across suite apps.
 * Browser-safe (localStorage + DOM). No Node imports.
 */

import {
  type SkinCatalog,
  type SkinPack,
  type SuitePrefs,
  BUILTIN_FX_IDS,
  CATALOG_SCHEMA,
  PREFS_SCHEMA,
  SKIN_SCHEMA,
  fontCss,
  resolveVl,
  validateSkinPack,
} from "./skin-pack";
import { applyMotionCss } from "./apply-motion-css";

const CACHE_SKINS = "forgesuite.skins.v1";
const CACHE_PREFS = "forgesuite.prefs.v1";
const CACHE_CATALOG = "forgesuite.catalog.v1";

export type TokenSystem = "pf" | "vl";

export interface RuntimeRegistry {
  suite: SkinPack[];
  user: SkinPack[];
  updatedAt: string;
}

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota */
  }
}

export function loadCachedRegistry(): RuntimeRegistry {
  try {
    const raw = lsGet(CACHE_SKINS);
    if (raw) {
      const j = JSON.parse(raw) as RuntimeRegistry;
      if (Array.isArray(j.suite)) return j;
    }
  } catch {
    /* */
  }
  return { suite: [], user: [], updatedAt: "" };
}

export function saveCachedRegistry(reg: RuntimeRegistry): void {
  lsSet(CACHE_SKINS, JSON.stringify(reg));
}

export function loadCachedPrefs(): SuitePrefs | null {
  try {
    const raw = lsGet(CACHE_PREFS);
    if (!raw) return null;
    const j = JSON.parse(raw) as SuitePrefs;
    if (j.schema === PREFS_SCHEMA || j.activeSkinId !== undefined) return j;
  } catch {
    /* */
  }
  return null;
}

export function saveCachedPrefs(prefs: SuitePrefs): void {
  lsSet(CACHE_PREFS, JSON.stringify(prefs));
}

export function loadCachedCatalog(): SkinCatalog | null {
  try {
    const raw = lsGet(CACHE_CATALOG);
    if (!raw) return null;
    return JSON.parse(raw) as SkinCatalog;
  } catch {
    return null;
  }
}

export function saveCachedCatalog(cat: SkinCatalog): void {
  lsSet(CACHE_CATALOG, JSON.stringify(cat));
}

export function allPacks(reg: RuntimeRegistry): SkinPack[] {
  const map = new Map<string, SkinPack>();
  for (const p of reg.suite) map.set(p.id, p);
  for (const p of reg.user) map.set(p.id, p); // user overrides suite same id
  return [...map.values()];
}

export function findPack(
  reg: RuntimeRegistry,
  id: string | null | undefined,
): SkinPack | null {
  if (!id) return null;
  return allPacks(reg).find((p) => p.id === id) ?? null;
}

/** Merge JSON packs from catalog API / files into registry */
export function ingestPacks(
  packs: unknown[],
  scope: "suite" | "user" = "suite",
): SkinPack[] {
  const out: SkinPack[] = [];
  for (const raw of packs) {
    const v = validateSkinPack({
      ...(typeof raw === "object" && raw ? raw : {}),
      scope,
      schema: SKIN_SCHEMA,
    });
    if (v.pack) out.push(v.pack);
  }
  return out;
}

export function applyPack(
  pack: SkinPack,
  system: TokenSystem = "pf",
  opts?: { productAccent?: string; storageKey?: string },
): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", pack.id);
  root.setAttribute("data-skin", pack.id);
  root.classList.toggle("dark", pack.mode === "dark");
  root.classList.toggle("light", pack.mode === "light");
  root.classList.toggle("pf-light", pack.mode === "light");

  if (system === "pf") {
    const fonts = fontCss(pack.fonts);
    const tokens: Record<string, string> = {
      ...pack.pf,
      "--pf-font-display": pack.pf["--pf-font-display"] || fonts.display,
      "--pf-font-body": pack.pf["--pf-font-body"] || fonts.body,
    };
    for (const [k, v] of Object.entries(tokens)) {
      if (v) root.style.setProperty(k, v);
    }
    if (opts?.productAccent) {
      root.style.setProperty("--product-accent", opts.productAccent);
    }
    setMetaThemeColor(pack.pf["--pf-bg-deep"]);
  } else {
    const vl = resolveVl(pack);
    for (const [k, v] of Object.entries(vl)) {
      if (v) root.style.setProperty(k, v);
    }
    // shared layout defaults used by VividLex
    root.style.setProperty("--vl-radius", "14px");
    root.style.setProperty("--vl-radius-sm", "10px");
    root.style.setProperty("--vl-sidebar-width", "320px");
    root.style.setProperty(
      "--vl-mono",
      '"Cascadia Code", "Consolas", "SF Mono", monospace',
    );
    setMetaThemeColor(vl["--vl-bg"]);
  }

  if (pack.motion?.layers?.length) {
    applyMotionCss(pack.motion);
  } else {
    applyFx(pack.defaultFx);
  }

  if (opts?.storageKey) {
    try {
      localStorage.setItem(opts.storageKey, pack.id);
    } catch {
      /* */
    }
  }
}

export function applyFx(fxId: string): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const id =
    reduced || !fxId
      ? "none"
      : (BUILTIN_FX_IDS as readonly string[]).includes(fxId)
        ? fxId
        : fxId;
  root.setAttribute("data-bg-fx", id);
}

function setMetaThemeColor(color: string): void {
  if (typeof document === "undefined") return;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", color);
}

/**
 * Fetch suite catalog JSON from a same-origin or absolute URL
 * (e.g. /skins/catalog.json or SkinForge /api/catalog).
 */
export async function fetchCatalog(url: string): Promise<SkinCatalog | null> {
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return null;
    const j = (await res.json()) as SkinCatalog;
    if (j.schema && j.schema !== CATALOG_SCHEMA && !Array.isArray(j.skins)) {
      return null;
    }
    saveCachedCatalog(j);
    return j;
  } catch {
    return null;
  }
}

const OFFICIAL_CATALOG =
  "https://proxy.vividlex.com/api/catalog/skins";

export const SUITE_SKINS_EVENT = "forgesuite-skins";

type CatalogPayload = {
  skins?: Array<{ id?: string; kind?: string; pack?: unknown }>;
  packs?: unknown[];
  motions?: Array<{ id?: string }>;
};

let officialSkinIds: Set<string> | null = null;
let officialMotionIdSet: Set<string> | null = null;

export function getOfficialSkinIds(): Set<string> | null {
  return officialSkinIds;
}

export function getOfficialMotionIds(): Set<string> | null {
  return officialMotionIdSet;
}

export function notifySuiteSkins(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(SUITE_SKINS_EVENT));
  } catch {
    /* ignore */
  }
}

async function fetchOfficialCatalog(): Promise<CatalogPayload | null> {
  try {
    const res = await fetch(OFFICIAL_CATALOG, { credentials: "omit" });
    if (!res.ok) return null;
    return (await res.json()) as CatalogPayload;
  } catch {
    return null;
  }
}

/** AccessHub catalog is source of truth. SkinForge full packs are optional extras. */
export async function fetchPacksFromApi(
  baseUrl: string,
): Promise<RuntimeRegistry> {
  const base = baseUrl.replace(/\/$/, "");
  const cached = loadCachedRegistry();
  const reg: RuntimeRegistry = {
    suite: [],
    user: cached.user || [],
    updatedAt: new Date().toISOString(),
  };
  const catalog = await fetchOfficialCatalog();
  const catalogPacks: unknown[] = [];
  const allowed = new Set<string>();
  if (catalog) {
    for (const p of catalog.packs || []) {
      catalogPacks.push(p);
      const pid = (p as { id?: string })?.id;
      if (pid) allowed.add(String(pid));
    }
    for (const s of catalog.skins || []) {
      if (s?.id) {
        allowed.add(String(s.id));
        if (s.id === "vl-aurora") allowed.add("aurora");
        if (s.id === "aurora") allowed.add("vl-aurora");
      }
      if (s?.pack) catalogPacks.push(s.pack);
    }
  }
  if (catalog) {
    officialSkinIds = allowed.size ? allowed : null;
    const mots = new Set<string>();
    for (const m of catalog.motions || []) {
      if (m?.id) mots.add(String(m.id));
    }
    officialMotionIdSet = mots.size ? mots : null;
  }
  if (catalogPacks.length) {
    reg.suite = ingestPacks(catalogPacks, "suite");
  }
  try {
    const res = await fetch(`${base}/api/skins?full=1`);
    if (res.ok) {
      const extra = ingestPacks((await res.json()) as unknown[], "suite");
      const byId = new Map(reg.suite.map((p) => [p.id, p]));
      for (const p of extra) {
        if (!byId.has(p.id) && (!allowed.size || allowed.has(p.id))) {
          byId.set(p.id, p);
        }
      }
      reg.suite = [...byId.values()];
    }
  } catch {
    /* catalog packs are enough */
  }
  if (allowed.size) {
    reg.suite = reg.suite.filter((p) => allowed.has(p.id));
  }
  saveCachedRegistry(reg);
  notifySuiteSkins();
  return reg;
}

export function setActivePrefs(
  activeSkinId: string | null,
  activeFxId: string | null = null,
): SuitePrefs {
  const prefs: SuitePrefs = {
    schema: PREFS_SCHEMA,
    activeSkinId,
    activeFxId,
    updatedAt: new Date().toISOString(),
  };
  saveCachedPrefs(prefs);
  return prefs;
}

/**
 * Bootstrap: cache → optional network → apply active.
 * `applyBuiltin(id)` is the app's existing applyTheme for compile-time skins.
 */
export async function bootstrapSuiteSkins(opts: {
  system: TokenSystem;
  applyBuiltin: (id: string) => void;
  getBuiltinDefault: () => string;
  storageKey?: string;
  catalogUrl?: string;
  apiBaseUrl?: string;
  productAccent?: string;
}): Promise<{ pack: SkinPack | null; id: string }> {
  let reg = loadCachedRegistry();

  try {
    reg = await fetchPacksFromApi(
      opts.apiBaseUrl || "https://proxy.vividlex.com",
    );
  } catch {
    if (opts.catalogUrl) await fetchCatalog(opts.catalogUrl);
  }

  const prefs = loadCachedPrefs();
  let preferred: string | null = prefs?.activeSkinId || null;
  if (!preferred && opts.storageKey) {
    preferred = lsGet(opts.storageKey);
  }

  const pack = findPack(reg, preferred);
  if (pack) {
    applyPack(pack, opts.system, {
      productAccent: opts.productAccent,
      storageKey: opts.storageKey,
    });
    if (prefs?.activeFxId) applyFx(prefs.activeFxId);
    return { pack, id: pack.id };
  }

  const fallback = preferred || opts.getBuiltinDefault();
  opts.applyBuiltin(fallback);
  return { pack: null, id: fallback };
}

export { BUILTIN_FX_IDS, SKIN_SCHEMA, PREFS_SCHEMA, CATALOG_SCHEMA };
